import { HttpRequest } from "libs/http-helper";
import { createContext, FC, ReactNode, useContext, useState } from "react";
import { Cart, Product } from "../models/Product"
import { useAuth } from "./AuthProvider";

export type CartContextType = {
    cart: Cart,
    cartLoading: boolean,
    getCart: () => Cart,
    getCartCount: () => number,
    addToCart: (p: Product, qty: number) => void,
    removeFromCart: (p: Product) => void,
    updateItem: (p: Product, qty: number) => void,
    clearCart: () => void,
    hasCanister: () => boolean
};

const CartContextDefaultState: CartContextType = {
    cart: {} as Cart,
    cartLoading: false,
    getCart: () => ({} as Cart),
    getCartCount: () => 0,
    addToCart: (p: Product, qty: number) => { },
    removeFromCart: (p: Product) => { },
    updateItem: (p: Product, qty: number) => { },
    clearCart: () => { },
    hasCanister: () => false
};

export const CartContext = createContext(CartContextDefaultState);

interface CartProviderProps {
    children: ReactNode;
}

const CartProvider: FC<CartProviderProps> = ({ children }) => {
    const key = 'airproducts.onlineshop.cart';
    const [items, setItems] = useState<Product[]>([]);
    const [cartLoading, setCartLoading] = useState(false);
    const { authenticating, isAuthenticated, getUserProperty, getToken } = useAuth();
    const cart = { items: items };
    const [cartProcessing, setCartProcessing] = useState(false);

    const addToCart = async (p: Product, qty: number) => {
        setCartProcessing(true);
        let existItems = getCartItems(), exists = false;
        for (let item of existItems) {
            if (!exists && item.materialNumberTrimmed === p.materialNumberTrimmed) {
                exists = true;
                item.quantity += qty;
                break;
            }
        }
        if (!exists) {
            p.quantity = qty;
            existItems.push(p);
        }
        await storeCartItems(existItems);
        setCartProcessing(false);
    };

    const removeFromCart = async (p: Product) => {
        setCartProcessing(true);
        let existItems = getCartItems();
        existItems.forEach((item, idx) => {
            if (item.materialNumberTrimmed === p.materialNumberTrimmed) {
                console.log(`Find item to delete: ${idx}`);
                existItems.splice(idx, 1);
            }
        });
        await storeCartItems(existItems);
        setCartProcessing(false);
    };

    const updateItem = async (p: Product, qty: number) => {
        setCartProcessing(true);
        let existItems = getCartItems();
        for (let item of existItems) {
            if (item.materialNumberTrimmed === p.materialNumberTrimmed) {
                item.quantity = qty;
                break;
            }
        }
        await storeCartItems(existItems);
        setCartProcessing(false);
    };

    const clearCart = async () => {
        setCartProcessing(true);
        await storeCartItems([]);
        setCartProcessing(false);
    };

    const getCart = (): Cart => {
        return JSON.parse(localStorage.getItem(key) as string);
    };

    const getCartCount = (): number => (getCart()?.items.map(i => i.quantity).reduce((a, b) => a + b, 0)) ?? 0;

    const getCartItems = () => {
        const _cart = getCart();
        return _cart?.items ? _cart?.items : [];
    };

    const storeCartItems = async (items: Product[]) => {
        setItems(items);
        localStorage.setItem(key, JSON.stringify({ items: items }));
        if (!authenticating && isAuthenticated()) {
            await HttpRequest<string>("post", `${import.meta.env.VITE_ONLINE_SHOP_RUN_TIME_API_URL}/cart`, { Products: items }, true, getToken());
        }
    };

    const hasCanister = (): boolean => {
        if (getCartCount() === 0) return false;
        return getCart()?.items.some(i => i.isCanister || i.isCanisterEquipment);
    };

    return <CartContext.Provider value={{ cart, cartLoading, getCart, getCartCount, addToCart, removeFromCart, updateItem, clearCart, hasCanister }}>
        {children}
    </CartContext.Provider>
};

export default CartProvider;
export const useCart = () => useContext(CartContext);